import { FC, useEffect, useState } from "react";
import { Button, Card, List, Space, Typography, message } from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

import { TvVideo, tvErrorMessage } from "../Lib";
import { getTvVideo, setTvCollectionItems } from "../Providers/tv-api";
import { VideoPicker, videoLabel } from "../Components";

type Props = {
  collectionId: number;
  videoIds: number[];
  onSaved: () => void;
};

/** Состав подборки: порядок в списке = порядок на сайте. Сохраняется целиком одной кнопкой. */
export const CollectionItems: FC<Props> = ({
  collectionId,
  videoIds,
  onSaved,
}) => {
  const [items, setItems] = useState<number[]>(videoIds);
  const [videos, setVideos] = useState<Record<number, TvVideo>>({});
  const [saving, setSaving] = useState(false);
  // Поле «Добавить видео» пересоздаём после выбора, чтобы оно снова было пустым.
  const [pickerKey, setPickerKey] = useState(0);
  const changed = items.join(",") !== videoIds.join(",");

  useEffect(() => setItems(videoIds), [videoIds]);

  // Названия видео подгружаем по одному: в подборке их немного (до 200).
  useEffect(() => {
    items
      .filter((id) => !videos[id])
      .forEach((id) =>
        getTvVideo(id)
          .then((v) => setVideos((prev) => ({ ...prev, [id]: v })))
          .catch(() => undefined)
      );
  }, [items, videos]);

  const move = (index: number, delta: number) => {
    const next = [...items];
    const [item] = next.splice(index, 1);
    next.splice(index + delta, 0, item);
    setItems(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await setTvCollectionItems(collectionId, items);
      message.success("Состав подборки сохранён");
      onSaved();
    } catch (e) {
      message.error(tvErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title={`Состав подборки · ${items.length}`}
      extra={
        <Space>
          {changed && (
            <Typography.Text type="warning">
              Есть несохранённые изменения
            </Typography.Text>
          )}
          <Button
            type="primary"
            onClick={save}
            loading={saving}
            disabled={!changed}
          >
            Сохранить состав
          </Button>
        </Space>
      }
    >
      <VideoPicker
        key={pickerKey}
        value={null}
        exclude={items}
        placeholder="Добавить видео: название или номер"
        onChange={(id, video) => {
          if (id && !items.includes(id)) {
            setItems([...items, id]);
            if (video) setVideos((prev) => ({ ...prev, [id]: video }));
          }
          setPickerKey((k) => k + 1);
        }}
      />
      <List
        style={{ marginTop: 16 }}
        dataSource={items}
        locale={{ emptyText: "В подборке пока нет видео" }}
        renderItem={(id, index) => (
          <List.Item
            actions={[
              <Button
                key="up"
                size="small"
                icon={<ArrowUpOutlined />}
                disabled={index === 0}
                onClick={() => move(index, -1)}
              />,
              <Button
                key="down"
                size="small"
                icon={<ArrowDownOutlined />}
                disabled={index === items.length - 1}
                onClick={() => move(index, 1)}
              />,
              <Button
                key="remove"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => setItems(items.filter((x) => x !== id))}
              />,
            ]}
          >
            <Typography.Text>
              {index + 1}. {videos[id] ? videoLabel(videos[id]) : `№${id}`}
            </Typography.Text>
          </List.Item>
        )}
      />
    </Card>
  );
};
